import './Timeline.scss';
import TimelinePlayhead from '../../WorkspaceAssets/timeline-cursor.png';
import { getCustomisationValue, getGroupData, updateCustomisationValue } from '../EditablePreview/EditablePreview';
import { testNodeVisibility } from '../EditablePreview/VisualisedComponents/VisualisedNode';

import WaveSurfer from 'wavesurfer.js';
import { useContext, useEffect, useRef, useState } from 'react';
import { TIMELINE_ARROW_SVG, WORKSPACE_LEFT_LIST_ICONS } from '../../WorkspaceAssets/Assets';
import { CampaignContext } from '../../../../../contexts/CampaignContext';
import LottieIcon from '../../../../common/LottieIcon';
import Toggle from '../ModuleList/customFields/toggle/toggleSwitch/Toggle';
import getIconCategoryClass, { getIconCategoryColor } from '../../../../common/iconCategory';
import { CLIP_CATEGORY_COLORS } from '../../../../../containers/TimelineEditor/Timeline';
import { IconJsxer } from '../../../../../helpers/IconHelper';

const waveImagesCache = {};
const clearOutOldWaveImagesFromCache = (featureId) => {
    // console.log("[timeline] Clearing out old wave images from cache for feature ID:", featureId);
    Object.keys(waveImagesCache).forEach(key => {
        if (!key.startsWith(`wf_${featureId}-`)) {
            delete waveImagesCache[key];
        }
    });
    // console.log("[timeline] Wave images cache after cleanup:", waveImagesCache);
};

const FeatureTimeline = (props) => {
    // console.log("Timeline component props:", props);
    useEffect(() => {
        clearOutOldWaveImagesFromCache(props.selectedFeatureId);
    }, [props.selectedFeatureId]);

    const playheadDragRef = useRef(null);
    const [dragLeft, setDragLeft] = useState(null); // px during drag, null when not dragging
    const timelineHolderRef = useRef(null);
    const scrollAnimRef = useRef(null);
    const mouseXRef = useRef(null);

    const handlePlayheadMouseDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const stepPx = 30.4 * rootFontSize;
        const originPx = 10.25 * rootFontSize;
        const startScrollLeft = timelineHolderRef.current?.scrollLeft ?? 0;
        playheadDragRef.current = {
            startX: e.clientX,
            startStep: props.timelinePlayheadStep,
            stepPx,
            originPx,
            startScrollLeft,
        };
        mouseXRef.current = e.clientX;

        const EDGE_THRESHOLD = 80; // px from edge to trigger auto-scroll
        const MAX_SCROLL_SPEED = 12; // px per frame

        const stepCount = props.selectedFeatureNode?.config?.timeline?.steps?.length ?? 1;

        const startAutoScroll = () => {
            if (!playheadDragRef.current || !timelineHolderRef.current) return;
            const rect = timelineHolderRef.current.getBoundingClientRect();
            const mx = mouseXRef.current;
            let speed = 0;
            if (mx < rect.left + EDGE_THRESHOLD) {
                speed = -MAX_SCROLL_SPEED * (1 - (mx - rect.left) / EDGE_THRESHOLD);
            } else if (mx > rect.right - EDGE_THRESHOLD) {
                speed = MAX_SCROLL_SPEED * (1 - (rect.right - mx) / EDGE_THRESHOLD);
            }
            if (speed !== 0) {
                const prevScrollLeft = timelineHolderRef.current.scrollLeft;
                timelineHolderRef.current.scrollLeft += speed;
                const actualScrollDelta = timelineHolderRef.current.scrollLeft - prevScrollLeft;
                if (actualScrollDelta !== 0) {
                    const { startX, startStep, stepPx, originPx, startScrollLeft } = playheadDragRef.current;
                    const scrollDelta = timelineHolderRef.current.scrollLeft - startScrollLeft;
                    const dx = mx - startX + scrollDelta;
                    const minLeft = timelineHolderRef.current.scrollLeft + originPx;
                    const maxLeft = originPx + (stepCount - 1) * stepPx + 30 * rootFontSize;
                    setDragLeft(Math.max(minLeft, Math.min(maxLeft, originPx + startStep * stepPx + dx)));
                    scrollAnimRef.current = requestAnimationFrame(startAutoScroll);
                } else {
                    // Already at scroll boundary — stop the loop
                    scrollAnimRef.current = null;
                }
            } else {
                scrollAnimRef.current = null;
            }
        };

        const onMove = (moveEvent) => {
            if (!playheadDragRef.current) return;
            mouseXRef.current = moveEvent.clientX;
            const { startX, startStep, stepPx, originPx, startScrollLeft } = playheadDragRef.current;
            const scrollDelta = (timelineHolderRef.current?.scrollLeft ?? 0) - startScrollLeft;
            const dx = moveEvent.clientX - startX + scrollDelta;
            const minLeft = (timelineHolderRef.current?.scrollLeft ?? 0) + originPx;
            const maxLeft = originPx + (stepCount - 1) * stepPx + 30 * rootFontSize;
            setDragLeft(Math.max(minLeft, Math.min(maxLeft, originPx + startStep * stepPx + dx)));

            // Cancel any running auto-scroll and restart based on current mouse position
            if (scrollAnimRef.current) {
                cancelAnimationFrame(scrollAnimRef.current);
                scrollAnimRef.current = null;
            }
            if (timelineHolderRef.current) {
                const rect = timelineHolderRef.current.getBoundingClientRect();
                if (moveEvent.clientX < rect.left + EDGE_THRESHOLD || moveEvent.clientX > rect.right - EDGE_THRESHOLD) {
                    scrollAnimRef.current = requestAnimationFrame(startAutoScroll);
                }
            }
        };

        const onUp = (upEvent) => {
            if (!playheadDragRef.current) return;
            if (scrollAnimRef.current) {
                cancelAnimationFrame(scrollAnimRef.current);
                scrollAnimRef.current = null;
            }
            const { startX, startStep, stepPx, startScrollLeft } = playheadDragRef.current;
            const scrollDelta = (timelineHolderRef.current?.scrollLeft ?? 0) - startScrollLeft;
            const dx = upEvent.clientX - startX + scrollDelta;
            const rawStep = startStep + dx / stepPx;
            const stepCount = props.selectedFeatureNode?.config?.timeline?.steps?.length ?? 1;
            const snapped = Math.max(0, Math.min(stepCount - 1, Math.round(rawStep)));
            props.setTimelinePlayheadStep(snapped);
            setDragLeft(null);
            playheadDragRef.current = null;
            mouseXRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const TimelineItem = (props) => {
        const node = props.node;
        if (testNodeVisibility(node)) {
            return null;
        }
        const steps = (1 + (node.config?.visualisationConfig?.endStep ?? node['end-step'] ?? 0) - (node.config?.visualisationConfig?.startStep ?? node['start-step'] ?? 0));
        const stepWidth = steps * 30 + (steps - 1) * 2 * 0.2; // Calculate the width based on the step range
        const positionRelativeToStep = props.positionRelativeToStep || 0;
        const extraClasses = props.extraClasses || '';
        // console.log("Rendering TimelineItem for node:", props, node, "with stepWidth:", stepWidth, "and positionRelativeToStep:", positionRelativeToStep);
        return (
            <div className={`v2-timeline-step${extraClasses !== '' ? ` ${extraClasses}` : ''} ${(props.visualisedNode === node || (props.visualisedNode === null && props.activeNode === node) || (props.visualisedNode === null && props.activeNode?.parent === node && node.dynamicSubNodes)) ? 'v2-timeline-step-active' : ''}`} style={{ width: `${stepWidth}rem`, marginLeft: `${0.2 + ((node.config?.visualisationConfig?.startStep - positionRelativeToStep) * 30.4)}rem` }} onClick={() => props.activateNodeFunction(node)}>
                {!node?.dynamicSubNodes && <>{node ? node.name : ''}</>}
                {node?.dynamicSubNodes && 
                    <>
                        <div className='v2-timeline-multi-data-label'>{node ? node.name : ''}</div>
                        <span className='v2-timeline-multi-data-label bottom'>[x{node.subNodes.length - 1}]</span>
                    </>
                }
            </div>
        )
    }

    const getFirstEnabledAudioFileIndex = (audioData, audioFileDataArray) => {
        const featureCustomisationVars = props.selectedFeatureNode?.customisationVars || {};
        let firstEnabledAudioFileIndex = audioData?.['audio-enabled'] ? -1 : 0;
        if (firstEnabledAudioFileIndex === -1 && audioFileDataArray?.length) {
            for (let i = 0; i < audioFileDataArray.length; i++) {
                const isEnabled = getCustomisationValue(featureCustomisationVars, audioData?.['audio-enabled'], i, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
                if (isEnabled) {
                    firstEnabledAudioFileIndex = i;
                    break;
                }
            }
        }
        return firstEnabledAudioFileIndex;
    }

    const enableAudioGroup = (audioData, audioFileDataArray) => {
        // console.log("Enabling audio group for audio data:", audioData, "with audio file data array:", audioFileDataArray);
        const featureCustomisationVars = props.selectedFeatureNode?.customisationVars || {};
        if (audioData?.['audio-enabled']) {
            const groupData = getGroupData(featureCustomisationVars, audioData?.['audio-enabled'], -1, true);
            for (let i = 0; i < groupData?.length ?? 1; i++) {
                const currentValue = getCustomisationValue(featureCustomisationVars, audioData?.['audio-enabled'], i, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
                if (!currentValue) {
                    updateCustomisationValue(featureCustomisationVars, audioData?.['audio-enabled'], true, i, null, props.selectedFeatureNode);
                }
            }
        }
    }

    const disableAudioGroup = (audioData, audioFileDataArray) => {
        // console.log("Disabling audio group for audio data:", audioData, "with audio file data array:", audioFileDataArray);
        const featureCustomisationVars = props.selectedFeatureNode?.customisationVars || {};
        let valChanged = false;
        if (audioData?.['audio-enabled']) {
            const groupData = getGroupData(featureCustomisationVars, audioData?.['audio-enabled'], -1, true);
            for (let i = 0; i < groupData?.length ?? 1; i++) {
                const currentValue = getCustomisationValue(featureCustomisationVars, audioData?.['audio-enabled'], i, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
                if (currentValue) {
                    updateCustomisationValue(featureCustomisationVars, audioData?.['audio-enabled'], false, i, null, props.selectedFeatureNode);
                    valChanged = true;
                }
            }
        }
        if (props.activeTimelineAudioGroup?.audioData === audioData && valChanged) {
            props.activateTimelineAudioGroup(null);
        }
    }

    const getFirstEnabledDisruptorIndex = (disruptorData, disruptorDataDataArray) => {
        // console.log("getFirstEnabledDisruptorIndex called with:", disruptorData, disruptorDataDataArray);
        const featureCustomisationVars = props.selectedFeatureNode?.customisationVars || {};
        let firstEnabledDisruptorIndex = disruptorData?.['disruptor-enabled'] ? -1 : 0;
        if (firstEnabledDisruptorIndex === 0) {
            const disruptorId = getCustomisationValue(featureCustomisationVars, disruptorData?.['disruptor-select'], 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? true;
            if (!disruptorId || disruptorId === '' || disruptorId?.trim?.() === '' || !disruptorDataDataArray?.[0]) {
                firstEnabledDisruptorIndex = -1;
            }
        }
        if (firstEnabledDisruptorIndex === -1 && disruptorDataDataArray?.length) {
            for (let i = 0; i < disruptorDataDataArray.length; i++) {
                const isEnabled = getCustomisationValue(featureCustomisationVars, disruptorData?.['disruptor-enabled'], i, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? true;
                if (isEnabled) {
                    const disruptorId = getCustomisationValue(featureCustomisationVars, disruptorData?.['disruptor-select'], i, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? true;
                    if (!disruptorId || disruptorId === '' || disruptorId?.trim?.() === '' || !disruptorDataDataArray?.[i]) {
                        continue;
                    }
                    firstEnabledDisruptorIndex = i;
                    break;
                }
            }
        }
        return firstEnabledDisruptorIndex;
    }

    const TimelineAudioItem = (props) => {
        // console.log("Rendering TimelineAudioItem with props:", props);
        const audioData = props.audioData;
        const steps = (1 + (audioData['end-step'] ?? 0) - (audioData['trigger-step'] ?? 0));
        const stepWidth = steps * 30 + (steps - 1) * 2 * 0.2; // Calculate the width based on the step range
        const positionRelativeToStep = props.positionRelativeToStep || 0;

        const featureCustomisationVars = props.selectedFeatureNode?.customisationVars || {};
        // console.log("TimelineAudioItem featureCustomisationVars:", featureCustomisationVars, audioData);
        const audioFileDataArray = getGroupData(featureCustomisationVars, audioData?.['audio-file'], -1);

        const firstEnabledAudioFileIndex = getFirstEnabledAudioFileIndex(audioData, audioFileDataArray);
        const audioFileData = getCustomisationValue(featureCustomisationVars, audioData?.['audio-file'], firstEnabledAudioFileIndex, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null;

        // Generate a unique container ID for the waveform, making sure to sanitize the audio name
        const generateWaveformContainerId = () => {
            let id = `wf_${props.selectedFeatureId}-${firstEnabledAudioFileIndex}-audio-waveform-${audioData.name.replace(/\s+/g, '-')}`.replace(/[^a-zA-Z0-9_-]/g, '');
            id += '_' + (typeof audioFileData === 'string' ? audioFileData : audioFileData?.originalFile ?? 'no-file').replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '');
            return id;
        }
        const [generatedWaveformContainerId, setGeneratedWaveformContainerId] = useState(generateWaveformContainerId());
        useEffect(() => {
            setGeneratedWaveformContainerId(generateWaveformContainerId());
            // console.log('[timeline, useEffect] Generated waveform container ID:', generatedWaveformContainerId);
        }, [audioData.name, props.selectedFeatureId, firstEnabledAudioFileIndex]);

        // console.log('Timeline Audio Item', audioFileData, generatedWaveformContainerId);

        const selectAudioGroup = () => {
            enableAudioGroup(audioData, audioFileDataArray);
            const audioGroupDataToActivate = {
                groupData: props.audioGroup,
                audioData: audioData,
            };
            props.activateTimelineAudioGroup(audioGroupDataToActivate);
        }

        const [wavesurferInstance, setWavesurferInstance] = useState(null);
        useEffect(() => {
            if (getFirstEnabledAudioFileIndex(audioData, audioFileDataArray) !== -1 && audioFileData && audioFileData.srcUrl) {
                if (!waveImagesCache[generatedWaveformContainerId]) {
                    // console.log('[timeline, useEffect] Creating waveform for', generatedWaveformContainerId, 'with URL', audioFileData.srcUrl);
                    const waveSurfer = WaveSurfer.create({
                        container: `#${generatedWaveformContainerId}`,
                        waveColor: '#BCA4FF',
                        progressColor: '#BCA4FF',
                        url: audioFileData.srcUrl,
                        height: 20,
                        // responsive: true,
                        normalize: true,
                        hideScrollbar: true,
                        barWidth: 2,
                    });
                    waveSurfer.on('ready', () => {
                        waveSurfer.exportImage().then((dataUrl) => {
                            // console.log('[timeline, useEffect] Waveform image data URL:', dataUrl);
                            waveImagesCache[generatedWaveformContainerId] = dataUrl[0];
                            // console.log('[timeline, useEffect] Cached waveform image for', generatedWaveformContainerId, waveImagesCache);
                        });
                    });
                    setWavesurferInstance(waveSurfer);
                    return () => {
                        // console.log('[timeline, useEffect] Destroying waveform for', generatedWaveformContainerId);
                        waveSurfer.destroy();
                        setWavesurferInstance(null);
                    }
                }
            }
        }, [audioFileData, generatedWaveformContainerId, props.forceUpdate]);


        // console.log("Rendering TimelineAudioItem for audio data:", audioData, "with audio file data:", audioFileData, props);

        return (
            <>
                {(firstEnabledAudioFileIndex !== -1 || props.activeTimelineAudioGroup?.audioData === audioData) && 
                    <div className={`v2-timeline-step audio-step ${(props.activeTimelineAudioGroup?.audioData === audioData) ? 'v2-timeline-step-active' : ''}`} style={{ width: `${stepWidth}rem`, marginLeft: `${0.2 + ((audioData['trigger-step'] - positionRelativeToStep) * 30.4)}rem` }} onClick={selectAudioGroup}>
                        <div className='v2-timeline-audio-waveform' id={generatedWaveformContainerId}>
                            {waveImagesCache[generatedWaveformContainerId] &&
                                <img src={waveImagesCache[generatedWaveformContainerId]} alt="Audio Waveform" />
                            }
                        </div>
                        <div className='v2-timeline-audio-label'>{audioData ? audioData.name : ''}</div>
                        <div className='v2-timeline-audio-label bottom'>[x{audioFileDataArray?.length ?? 1}]</div>
                        {audioData?.['audio-enabled'] &&
                            <div className='v2-timeline-audio-toggle'>
                                {/*<Toggle value={true} isOn={true} onToggle={(e) => {e.stopPropagation(); disableAudioGroup(audioData, audioFileDataArray)}} />*/}
                                <div className="v2-red-circle-delete-icon-button" onClick={(e) => {e.stopPropagation(); disableAudioGroup(audioData, audioFileDataArray)}}>
                                    {IconJsxer.GetIcon(IconJsxer.ICONS.trash, IconJsxer.ICON_STYLES.roundPanelButtonSmall, 'delete-icon')}
                                </div>
                            </div>
                        }
                    </div>
                }
                {(firstEnabledAudioFileIndex === -1 && props.activeTimelineAudioGroup?.audioData !== audioData) && 
                    <div className={`v2-timeline-step audio-step empty`} style={{ width: `${stepWidth}rem`, marginLeft: `${0.2 + ((audioData['trigger-step'] - positionRelativeToStep) * 30.4)}rem` }}>
                        <img src={WORKSPACE_LEFT_LIST_ICONS.add} alt="Add Audio" className='plus-icon' onClick={selectAudioGroup} />
                        <span className='v2-timeline-audio-label light-text' style={{marginLeft: '2rem', marginTop: '0.65rem'}}>{audioData ? audioData.name : ''}</span>
                    </div>
                }
            </>
        )
    }

    const TimelineDisruptorItem = (props) => {
        const [campaignContext, setCampaignContext] = useContext(CampaignContext);

        const disruptorData = props.disruptorData;
        const steps = (1 + (disruptorData.config?.visualisationConfig?.endStep ?? disruptorData['end-step'] ?? 0) - (disruptorData.config?.visualisationConfig?.triggerStep ?? disruptorData['trigger-step'] ?? 0));
        const stepWidth = steps * 30 + (steps - 1) * 2 * 0.2; // Calculate the width based on the step range
        const positionRelativeToStep = props.positionRelativeToStep || 0;

        const featureCustomisationVars = props.selectedFeatureNode?.customisationVars || {};
        // console.log("TimelineDisruptorItem featureCustomisationVars:", featureCustomisationVars, disruptorData);
        const disruptorDataArray = getGroupData(featureCustomisationVars, disruptorData?.['disruptor-enabled'] ?? disruptorData?.['disruptor-select'], -1);

        const firstEnabledDisruptorIndex = getFirstEnabledDisruptorIndex(disruptorData, disruptorDataArray);
        let disruptorLabel ='<No Disruptor Selected>';
        const disruptorId = getCustomisationValue(featureCustomisationVars, disruptorData?.['disruptor-select'], firstEnabledDisruptorIndex, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null;
        if (disruptorId && disruptorDataArray && disruptorDataArray.length > firstEnabledDisruptorIndex) {
            const disruptorFeature = campaignContext?.campaignData?.features?.find(feature => disruptorId?.startsWith?.(feature._id));
            disruptorLabel = disruptorFeature ? disruptorFeature?.contentLabel || disruptorFeature.feature.featureName : '<Unknown Disruptor>';
        }

        const selectDisruptorGroup = () => {
            const disruptorGroupDataToActivate = {
                groupData: props.disruptorGroup,
                disruptorData: disruptorData,
            };
            props.activateTimelineDisruptorGroup(disruptorGroupDataToActivate);
        }
        return (
            <>
                {(firstEnabledDisruptorIndex !== -1 || props.activeTimelineDisruptorGroup?.disruptorData === disruptorData) &&
                    <div className={`v2-timeline-step audio-step ${(props.activeTimelineDisruptorGroup?.disruptorData === disruptorData) ? 'v2-timeline-step-active' : ''}`} style={{ width: `${stepWidth}rem`, marginLeft: `${0.2 + ((disruptorData.config?.visualisationConfig?.triggerStep ?? disruptorData['trigger-step'] - positionRelativeToStep) * 30.4)}rem` }} onClick={selectDisruptorGroup}>
                        <div className='v2-timeline-audio-label'>{disruptorData ? disruptorData.name : ''}</div>
                        <div className='v2-timeline-audio-label bottom'>{disruptorLabel}{(disruptorDataArray?.length ?? 1) > 1 && <>{` [+${(disruptorDataArray?.length  ?? 1) - 1}]`}</>}</div>
                    </div>
                }
                {(firstEnabledDisruptorIndex === -1 && props.activeTimelineDisruptorGroup?.disruptorData !== disruptorData) &&
                    <div className={`v2-timeline-step audio-step empty`} style={{ width: `${stepWidth}rem`, marginLeft: `${0.2 + ((disruptorData.config?.visualisationConfig?.triggerStep ?? disruptorData['trigger-step'] - positionRelativeToStep) * 30.4)}rem` }}>
                        <img src={WORKSPACE_LEFT_LIST_ICONS.add} alt="Add Disruptor" className='plus-icon' onClick={selectDisruptorGroup} />
                        <span className='v2-timeline-audio-label light-text' style={{marginLeft: '2rem'}}>{disruptorData ? disruptorData.name : ''}</span>
                    </div>
                }
            </>
        )
    }

    const organiseTimelineItemsIntoRows = (items, isDisruptorGroup = false) => {
        const rows = [[]];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const firstEnabledAudioFileIndex = getFirstEnabledAudioFileIndex(item, getGroupData(props.selectedFeatureNode?.customisationVars || {}, item?.['audio-enabled'] ?? item?.['audio-file'], -1));
            const firstEnabledDisruptorIndex = getFirstEnabledDisruptorIndex(item, getGroupData(props.selectedFeatureNode?.customisationVars || {}, item?.['disruptor-enabled'] ?? item?.['disruptor-select'], -1));
            const itemStart = item.config?.visualisationConfig?.startStep ?? item['start-step'] ?? item.config?.visualisationConfig?.triggerStep ?? item['trigger-step'] ?? 0;
            const itemEnd = (isDisruptorGroup && (firstEnabledDisruptorIndex !== -1 || props.activeTimelineDisruptorGroup?.disruptorData === item)) ? (item.config?.visualisationConfig?.endStep ?? item['end-step'] ?? 0) : (firstEnabledAudioFileIndex !== -1 || props.activeTimelineAudioGroup?.audioData === item) ? item.config?.visualisationConfig?.endStep ?? item['end-step'] ?? 0 : itemStart;
            let placed = false;
            if (item?.config?.groupSettings?.visualise === false || item.hideFromTimeline) {
                continue;
            }
            for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
                const row = rows[rowIndex];
                let overlap = false;
                for (let j = 0; j < row.length; j++) {
                    const rowItem = row[j];
                    const rowFirstEnabledAudioFileIndex = getFirstEnabledAudioFileIndex(rowItem, getGroupData(props.selectedFeatureNode?.customisationVars || {}, rowItem?.['audio-enabled'] ?? rowItem?.['audio-file'], -1));
                    const rowFirstEnabledDisruptorIndex = getFirstEnabledDisruptorIndex(rowItem, getGroupData(props.selectedFeatureNode?.customisationVars || {}, rowItem?.['disruptor-enabled'] ?? rowItem?.['disruptor-select'], -1));
                    const rowItemStart = rowItem.config?.visualisationConfig?.startStep ?? rowItem['start-step'] ?? rowItem.config?.visualisationConfig?.triggerStep ?? rowItem['trigger-step'] ?? 0;
                    const rowItemEnd = (isDisruptorGroup && (rowFirstEnabledDisruptorIndex !== -1 || props.activeTimelineDisruptorGroup?.disruptorData === rowItem)) ? (rowItem.config?.visualisationConfig?.endStep ?? rowItem['end-step'] ?? 0) : (rowFirstEnabledAudioFileIndex !== -1 || props.activeTimelineAudioGroup?.audioData === rowItem) ? rowItem.config?.visualisationConfig?.endStep ?? rowItem['end-step'] ?? 0 : rowItemStart;
                    if (itemStart <= rowItemEnd && itemEnd >= rowItemStart) {
                        overlap = true;
                        break;
                    }
                }
                if (!overlap) {
                    row.push(item);
                    placed = true;
                    break;
                }
            }
            if (!placed) {
                rows.push([item]);
            }
        }

        // Sort the rows based on the start step of their items
        rows.forEach(row => {
            row.sort((a, b) => {
                const aStart = a.config?.visualisationConfig?.startStep ?? a['start-step'] ?? a.config?.visualisationConfig?.triggerStep ?? a['trigger-step'] ?? 0;
                const bStart = b.config?.visualisationConfig?.startStep ?? b['start-step'] ?? b.config?.visualisationConfig?.triggerStep ?? b['trigger-step'] ?? 0;
                return aStart - bStart;
            });
        });

        return rows;
    }

    const [nodeRows, setNodeRows] = useState(props.selectedFeatureNode ? organiseTimelineItemsIntoRows(props.selectedFeatureNode.subNodes || []) : []);
    useEffect(() => {
        if (props.selectedFeatureNode) {
            setNodeRows(organiseTimelineItemsIntoRows(props.selectedFeatureNode.subNodes || []));
        } else {
            setNodeRows([]);
        }
        // console.log("[timeline, useEffect] Organised timeline node rows:", nodeRows);
    }, [props.selectedFeatureId]);

    const [audioGroupRows, setAudioGroupRows] = useState([]);
    useEffect(() => {
        if (props.selectedFeatureNode?.config?.timeline?.audioGroups) {
            const newAudioGroupRows = [];
            props.selectedFeatureNode.config.timeline.audioGroups.forEach((audioGroup, groupIndex) => {
                const audioItems = audioGroup.audios || [];
                const organisedAudioRows = organiseTimelineItemsIntoRows(audioItems);
                newAudioGroupRows[groupIndex] = organisedAudioRows;
            });
            setAudioGroupRows(newAudioGroupRows);
        } else {
            setAudioGroupRows([]);
        }
        // console.log("[timeline, useEffect] Organised timeline audio group rows:", audioGroupRows, props.activeTimelineAudioGroup);
    }, [props.selectedFeatureId, props.visualisedNode, props.activeTimelineAudioGroup]);

    const [disruptorGroupRows, setDisruptorGroupRows] = useState([]);
    useEffect(() => {
        if (props.selectedFeatureNode?.config?.timeline?.disruptorGroups) {
            const newDisruptorGroupRows = [];
            props.selectedFeatureNode.config.timeline.disruptorGroups.forEach((disruptorGroup, groupIndex) => {
                const disruptorItems = disruptorGroup.disruptors || [];
                const organisedDisruptorRows = organiseTimelineItemsIntoRows(disruptorItems, true);
                newDisruptorGroupRows[groupIndex] = organisedDisruptorRows;
            });
            setDisruptorGroupRows(newDisruptorGroupRows);
        } else {
            setDisruptorGroupRows([]);
        }
        // console.log("[timeline, useEffect] Organised timeline disruptor group rows:", disruptorGroupRows, props.activeTimelineDisruptorGroup);
    }, [props.selectedFeatureId, props.visualisedNode, props.activeTimelineDisruptorGroup]);

    // console.log("Organised timeline node rows:", nodeRows);
    // console.log("Organised timeline audio group rows:", audioGroupRows);

    const rawCategory = props.selectedFeatureNode?.component?.iconCategory || props.selectedFeatureNode?.component?.featureCategory;
    const iconCategory = Array.isArray(rawCategory)
    ? rawCategory[0]
    : rawCategory;
    const categoryClass = getIconCategoryClass(iconCategory);
    const clipColor = getIconCategoryColor(iconCategory);
    const catSlug = getIconCategoryClass(iconCategory);
    const clipColors = CLIP_CATEGORY_COLORS[catSlug] || CLIP_CATEGORY_COLORS['misc'];

    return (
        <div className='v2-timeline' style={{"--timelineHeight": props.timelineHeight ? `${props.timelineHeight}px` : '50rem'}}>
            
            <div className="timeline-toolbar">
                <div className="timeline-title">
                    {props.selectedFeatureId &&
                    <>
                        <span>Feature Timeline</span>
                        <button
                            className={`play-button${props.selectedFeatureNode?.isLegacy ? ' play-button--disabled' : ''}`}
                            onClick={props.selectedFeatureNode?.isLegacy ? undefined : props.previewThisCustomisation}
                            disabled={props.selectedFeatureNode?.isLegacy}
                            title={props.selectedFeatureNode?.isLegacy ? 'Live Preview is not available for legacy features. Use the legacy edit page to preview.' : undefined}
                        >
                            <span className="play-icon">▶</span>
                            Live Preview
                        </button>
                    </>
                    }
                    {!props.selectedFeatureId && 
                        <span>No Feature Selected</span>
                    }
                </div>
                {/* <div className="timeline-controls">
                    <div className="zoom-control">
                        <button className="zoom-out" onClick={onZoomOut}>-</button>
                        <span className="zoom-level">{Math.round((zoomLevel ?? 1) * 100)}%</span>
                        <button className="zoom-in" onClick={onZoomIn}>+</button>
                    </div>
                </div> */}
            </div>

            {/* <div className="v2-timeline-row">
                <h4 className="v2-timeline-title">Feature Timeline</h4>
            </div> */}

            {props.selectedFeatureNode && 
                <div className="v2-timeline-holder" ref={timelineHolderRef}>
                    <div className='v2-sticky-playhead-holder'>
                        <div className='rel'>
                            <div
                                className='v2-timeline-playhead'
                                style={{
                                    left: dragLeft !== null ? `${dragLeft}px` : `${10.25 + props.timelinePlayheadStep * 30.4}rem`,
                                    top: '2.9rem',
                                    pointerEvents: 'auto',
                                    cursor: dragLeft !== null ? 'grabbing' : 'grab',
                                }}
                                onMouseDown={handlePlayheadMouseDown}
                            >
                                <div className='v2-playhead-arrow'>{TIMELINE_ARROW_SVG}</div>
                            </div>
                        </div>
                    </div>
                    <div className="v2-timeline-row v2-timeline-sticky-header">
                        <div className="v2-timeline-row-label">
                            Feature
                        </div>
                        <div className="v2-timeline-inner-row">
                            <div className="v2-feature-name-block" style={{backgroundColor: clipColor, borderColor: clipColors?.border || clipColor}}>
                                <LottieIcon icon={props.selectedFeatureNode?.component?.icon} varKey={props.selectedFeatureNode?.component?.varKey} size={24} />
                                {props.selectedFeatureNode.name}
                            </div>
                        </div>
                    </div>
                    <div className="v2-timeline-row v2-steps-sticky-header">
                        <div className="v2-timeline-row-label">
                            Steps
                        </div>
                        <div className="v2-timeline-inner-row v2-timeline-step-row">
                            {props.selectedFeatureNode.config?.timeline?.steps && props.selectedFeatureNode.config.timeline.steps.length > 0 &&
                                props.selectedFeatureNode.config.timeline.steps.map((step, index) => (
                                    <div key={index} className="v2-timeline-step step" onClick={() => props.setTimelinePlayheadStep(index)}>
                                        {step}
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    <div className="v2-timeline-row">
                        <div className="v2-timeline-row-label">
                            Views
                        </div>
                        <div className="v2-timeline-inner-column">
                            {nodeRows && nodeRows.length > 0 &&
                                nodeRows.map((row, rowIndex) => (
                                    <div className='v2-timeline-inner-row' key={rowIndex}>
                                        {row.map((node, index) => {
                                            let positionRelativeToStep = 0;
                                            if (index > 0) {
                                                const prevItem = row[index - 1];
                                                const prevItemEndStep = prevItem.config?.visualisationConfig?.endStep ?? prevItem['end-step'] ?? 0;
                                                // We will need to add one so we position this item after the last one
                                                positionRelativeToStep = prevItemEndStep + 1;
                                            }
                                            return (
                                                <TimelineItem extraClasses="view" key={index} node={node} visualisedNode={props.visualisedNode} activeNode={props.activeNode} activateNodeFunction={props.activateNodeFunction} positionRelativeToStep={positionRelativeToStep} />
                                            )
                                        })}
                                    </div>
                                ))
                            }
                            {/*props.selectedFeatureNode.subNodes && props.selectedFeatureNode.subNodes.length > 0 &&
                                props.selectedFeatureNode.subNodes.map((node, index) => {
                                    // console.log("Rendering timeline item for view node:", node);
                                    if (node?.config?.groupSettings?.visualise === false) {
                                        return null;
                                    }
                                    return (
                                        <div className='v2-timeline-inner-row' key={index}>
                                            <TimelineItem node={node} visualisedNode={props.visualisedNode} activateNodeFunction={props.activateNodeFunction} />
                                        </div>
                                    )
                                })
                            */}
                        </div>
                    </div>
                    {props.selectedFeatureNode.config?.timeline?.audioGroups?.length > 0 &&
                        props.selectedFeatureNode.config?.timeline?.audioGroups.map((audioGroup, groupIndex) => {
                            if (audioGroup.audios && audioGroup.audios.length > 0) {
                                return (
                                    <div className="v2-timeline-row" key={groupIndex}>
                                        <div className="v2-timeline-row-label">
                                            Audio<br />[{audioGroup.name}]
                                        </div>
                                        <div className="v2-timeline-inner-column">
                                            {audioGroupRows[groupIndex] && audioGroupRows[groupIndex].length > 0 && (
                                                audioGroupRows[groupIndex].map((row, rowIndex) => (
                                                    <div className='v2-timeline-inner-row' key={groupIndex + '_' + rowIndex}>
                                                        {row.map((audio, audioIndex) => {
                                                            let positionRelativeToStep = 0;
                                                            if (audioIndex > 0) {
                                                                const prevItem = row[audioIndex - 1];
                                                                const prevItemEndStep = prevItem.config?.visualisationConfig?.endStep ?? prevItem['end-step'] ?? 0;
                                                                // We will need to add one so we position this item after the last one
                                                                positionRelativeToStep = prevItemEndStep + 1;
                                                            }
                                                            return (
                                                                <TimelineAudioItem key={groupIndex + '_' + rowIndex + '_' + audioIndex} audioGroup={audioGroup} audioData={audio} audioIndex={audioIndex} activateTimelineAudioGroup={props.activateTimelineAudioGroup} activeTimelineAudioGroup={props.activeTimelineAudioGroup} selectedFeatureNode={props.selectedFeatureNode} selectedFeatureId={props.selectedFeatureId} positionRelativeToStep={positionRelativeToStep} forceUpdate={props.forceUpdate} />
                                                            );
                                                        })}
                                                    </div>
                                                ))
                                            )}
                                            {/*audioGroup.audios.map((audio, audioIndex) => (
                                                <div className='v2-timeline-inner-row' key={audioIndex}>
                                                    <TimelineAudioItem audioData={audio} audioIndex={audioIndex} selectedFeatureNode={props.selectedFeatureNode} selectedFeatureId={props.selectedFeatureId} visualisedNode={props.visualisedNode} />
                                                </div>
                                            ))*/}
                                        </div>
                                    </div>
                                )
                            }
                            return null;
                        })
                    }
                    {props.selectedFeatureNode.config?.timeline?.disruptorGroups?.length > 0 &&
                        props.selectedFeatureNode.config?.timeline?.disruptorGroups.map((disruptorGroup, groupIndex) => {
                            if (disruptorGroup.disruptors && disruptorGroup.disruptors.length > 0) {
                                return (
                                    <div className="v2-timeline-row" key={groupIndex}>
                                        <div className="v2-timeline-row-label">
                                            Disruptors<br />[{disruptorGroup.name}]
                                        </div>
                                        <div className="v2-timeline-inner-column">
                                            {disruptorGroupRows[groupIndex] && disruptorGroupRows[groupIndex].length > 0 && (
                                                disruptorGroupRows[groupIndex].map((row, rowIndex) => (
                                                    <div className='v2-timeline-inner-row' key={groupIndex + '_' + rowIndex}>
                                                        {row.map((disruptor, disruptorIndex) => {
                                                            let positionRelativeToStep = 0;
                                                            if (disruptorIndex > 0) {
                                                                const prevItem = row[disruptorIndex - 1];
                                                                const prevItemEndStep = prevItem.config?.visualisationConfig?.endStep ?? prevItem['end-step'] ?? 0;
                                                                // We will need to add one so we position this item after the last one
                                                                positionRelativeToStep = prevItemEndStep + 1;
                                                            }
                                                            return (
                                                                <TimelineDisruptorItem key={groupIndex + '_' + rowIndex + '_' + disruptorIndex} disruptorGroup={disruptorGroup} disruptorData={disruptor} disruptorIndex={disruptorIndex} activateTimelineDisruptorGroup={props.activateTimelineDisruptorGroup} activeTimelineDisruptorGroup={props.activeTimelineDisruptorGroup} selectedFeatureNode={props.selectedFeatureNode} selectedFeatureId={props.selectedFeatureId} positionRelativeToStep={positionRelativeToStep} />
                                                            );
                                                        })}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )
                            }
                            return null;
                        })
                    }
                </div>
            }
        </div>
    );
}

export default FeatureTimeline;
